import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, QrCode, Copy, Check, CheckCircle2, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const errText = (e, f) => (typeof e.response?.data?.detail === 'string' ? e.response.data.detail : f);

const useCountdown = (iso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const left = Math.max(0, new Date(iso).getTime() - now);
  const h = Math.floor(left / 3600000); const m = Math.floor((left % 3600000) / 60000); const s = Math.floor((left % 60000) / 1000);
  return { expired: iso && left === 0, label: `${h ? `${h}h ` : ''}${String(m).padStart(2, '0')}min ${String(s).padStart(2, '0')}s` };
};

export const PixCode = ({ pix, color, onExpired }) => {
  const [copied, setCopied] = useState(false);
  const { expired, label } = useCountdown(pix.expires_at);
  useEffect(() => { if (expired) onExpired(); }, [expired, onExpired]);
  const copy = async () => { await navigator.clipboard.writeText(pix.qr_code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <div data-testid="pix-code" style={{ textAlign: 'center' }}>
      <p style={{ fontSize: '0.85rem', color: '#4a5568', marginBottom: '0.6rem' }}>Abra o app do seu banco, escolha <strong>Pix → Ler QR Code</strong> ou use o código copia e cola.</p>
      {pix.qr_code_base64 && <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" data-testid="pix-qr-image"
        style={{ width: 196, height: 196, margin: '0 auto', border: '1px solid #e2e8f0', borderRadius: 10, padding: 6, background: 'white' }} />}
      <div style={{ display: 'flex', gap: 6, marginTop: '0.75rem' }}>
        <input readOnly value={pix.qr_code || ''} onFocus={(e) => e.currentTarget.select()} data-testid="pix-copy-paste-input"
          style={{ flex: 1, minWidth: 0, fontSize: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem', background: '#f7fafc', color: '#4a5568' }} />
        <button type="button" onClick={copy} data-testid="pix-copy-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', borderRadius: 8, padding: '0 0.8rem', background: color, color: 'white', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>
          {copied ? <><Check className="w-4 h-4" />Copiado</> : <><Copy className="w-4 h-4" />Copiar</>}
        </button>
      </div>
      <p data-testid="pix-total" style={{ marginTop: '0.75rem', fontWeight: 700, color: '#2D3748' }}>{pix.meses > 1 ? `${pix.meses} meses · ` : ''}{BRL(pix.total)}</p>
      <p data-testid="pix-expires" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: '#718096' }}><Clock className="w-3 h-3" />Expira em {label}</p>
      <p data-testid="pix-waiting" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, fontSize: '0.8rem', color: color, marginTop: '0.6rem' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Aguardando o pagamento... a liberação é automática.
      </p>
    </div>
  );
};

export const PixPending = ({ editorId, token, pix, color = '#68AAB0', onDone, onExpired, onBack, onPaid }) => {
  const [paid, setPaid] = useState(null);
  useEffect(() => {
    if (paid) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/reader/pix/${editorId}/${pix.payment_id}`, { headers: { Authorization: `Bearer ${token}` } });
        if (data.active) { setPaid(data); clearInterval(t); onPaid?.(); }
      } catch (e) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [pix, paid, editorId, token]);
  if (!paid && pix.pending_card) return <p data-testid="card-pending" style={{ display: "flex", gap: 8, alignItems: "center", color: "#4a5568", padding: "1rem 0" }}><Loader2 className="w-5 h-5 animate-spin" />{pix.message}</p>;
  if (!paid) return (<><PixCode pix={pix} color={color} onExpired={onExpired} />{onBack && <button type="button" onClick={onBack} data-testid="pix-back-btn" style={{ display: 'block', margin: '0.75rem auto 0', background: 'none', border: 'none', color: '#4a5568', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.85rem' }}>Voltar e mudar forma de pagamento</button>}</>);
  return (
    <div data-testid="pix-paid" style={{ textAlign: 'center', padding: '1rem 0' }}>
      <CheckCircle2 className="w-10 h-10" style={{ color: '#38a169', margin: '0 auto 0.5rem' }} />
      <p style={{ fontWeight: 600, color: '#2D3748' }}>Pagamento confirmado!</p>
      <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>Acesso liberado até {new Date(paid.subscription_end).toLocaleDateString('pt-BR')}.</p>
      <button type="button" onClick={onDone} data-testid="pix-continue-btn"
        style={{ background: color, color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer' }}>Começar a ler</button>
    </div>
  );
};

export const PixCheckout = ({ editorId, token, monthly, color = '#68AAB0', onDone }) => {
  const [pix, setPix] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [paid, setPaid] = useState(null);
  const timer = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!pix || paid) return undefined;
    timer.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/reader/pix/${editorId}/${pix.payment_id}`, { headers });
        if (data.active) { setPaid(data); clearInterval(timer.current); }
      } catch (e) { /* keep polling */ }
    }, 5000);
    return () => clearInterval(timer.current);
  }, [pix, paid]);

  const generate = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await axios.post(`${API}/reader/pix/${editorId}`, { meses: 1 }, { headers });
      setPix(data);
    } catch (e) {
      setError(errText(e, 'Não foi possível gerar o Pix agora.'));
    } finally { setBusy(false); }
  };

  if (paid) {
    return (
      <div data-testid="pix-paid" style={{ textAlign: 'center', padding: '1rem 0' }}>
        <CheckCircle2 className="w-10 h-10" style={{ color: '#38a169', margin: '0 auto 0.5rem' }} />
        <p style={{ fontWeight: 600, color: '#2D3748' }}>Pagamento confirmado!</p>
        <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>Acesso liberado até {new Date(paid.subscription_end).toLocaleDateString('pt-BR')}.</p>
        <button type="button" onClick={onDone} data-testid="pix-continue-btn"
          style={{ background: color, color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.25rem', fontWeight: 600, cursor: 'pointer' }}>Começar a ler</button>
      </div>
    );
  }
  if (pix) return <PixCode pix={pix} color={color} onExpired={() => setPix(null)} />;
  return (
    <div data-testid="pix-checkout">
      <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '0.9rem' }}>
        Assinatura mensal no Pix, até você cancelar: todo mês enviamos por e-mail o Pix do mês seguinte (avisos 5 dias e 1 dia antes do vencimento).
      </p>
      <button type="button" onClick={generate} disabled={busy} data-testid="pix-generate-btn"
        style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, height: 44, border: 'none', borderRadius: 8, background: color, color: 'white', fontWeight: 700, cursor: 'pointer' }}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><QrCode className="w-4 h-4" />Gerar Pix de {BRL(monthly)}</>}
      </button>
      {error && <p data-testid="pix-error" style={{ color: '#c53030', fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</p>}
    </div>
  );
};
